import { getLocale, useTranslation, translate } from '@rhino-project/core/i18n';
import { Link } from '@tanstack/react-router';
import { BaseAuthedPage, MaxWidth } from '@rhino-project/ui-heroui';
import { useOverlayState, Typography } from '@heroui/react';
import {
  useBaseOwnerId,
  useBaseOwner,
  useFeature,
  useHasRoleOf,
  useModel,
  useModelIndex
} from '@rhino-project/core/hooks';
import { networkApiCallOnlyData } from '@rhino-project/core/lib';
import { useQuery } from '@tanstack/react-query';
import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
import { Icon } from '@iconify/react';
import { Button, Card } from '@heroui/react';
import OtsSidebarForm from '../components/ots/OtsSidebarForm';
import SeasonSidebarForm from '../components/ots/SeasonSidebarForm';
import { useCurrentTeam, useActiveSeasons } from '../hooks/ots';
import { getYouTubeThumbnail } from '../utils/youtube';
import type { RhinoRead } from '@rhino-project/core';

const todayIso = () => today(getLocalTimeZone()).toString();

const formatDate = (iso?: string | null) => {
  if (!iso) return '';
  return parseDate(iso)
    .toDate(getLocalTimeZone())
    .toLocaleDateString(getLocale().replaceAll('_', '-'), {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
};

/* ---------- shared header (matches the Planning dashboard cards) ---------- */

const CardHeader = ({
  title,
  to,
  params
}: {
  title: string;
  to?: string;
  params?: Record<string, string>;
}) => (
  <Card.Header className="flex-row justify-between items-center">
    <Card.Title>{title}</Card.Title>
    {to && (
      <Link to={to as never} params={params as never}>
        <Icon icon="bi:arrow-right-circle" className="text-xl text-muted" />
      </Link>
    )}
  </Card.Header>
);

/* ---------- latest activity (scoped to the active season) ---------- */

export const ActivityCard = ({ teamSeasonId }: { teamSeasonId: number }) => {
  const { t } = useTranslation();

  // Activity for players currently on this season's roster. Events have no
  // team_season link (Event only belongs_to :player), so we filter through the
  // player → rosters → team_season_id reflection path in a single request.
  const since = today(getLocalTimeZone()).subtract({ days: 90 }).toString();

  const { resources: events } = useModelIndex('event', {
    filter: {
      player: { rosters: { team_season_id: teamSeasonId } },
      created_at: { gteq: since }
    },
    order: ['-created_at'],
    limit: 5,
    queryOptions: { enabled: !!teamSeasonId }
  });

  return (
    <Card>
      <CardHeader title={t('frontend.messages.latest_activity')} />
      <Card.Content>
        {events?.results && events.results.length > 0 ? (
          <ul className="space-y-3">
            {events.results.map((e) => {
              const event = e;
              return (
                <li key={e.id} className="flex items-center gap-3">
                  <Icon
                    icon="bi:check-circle-fill"
                    className="text-success text-lg shrink-0"
                  />
                  <span className="text-foreground/80">
                    {event.display_name as string}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-muted">
            {t('frontend.messages.no_recent_activity')}
          </p>
        )}
      </Card.Content>
    </Card>
  );
};

/* ---------- next up (practice plans + prep videos) ---------- */

type NextUpItem = {
  key: string;
  kind: 'plan' | 'video';
  title: string;
  date?: string | null;
  href?: string;
  thumb?: string;
};

const typeMeta = (): Record<
  NextUpItem['kind'],
  { icon: string; label: string }
> => ({
  plan: {
    icon: 'bi:clipboard',
    label: translate('frontend.messages.practice_plan')
  },
  video: {
    icon: 'bi:play-fill',
    label: translate('frontend.messages.prep_video')
  }
});

const NextUpRow = ({ item }: { item: NextUpItem }) => {
  useTranslation();
  const meta = typeMeta()[item.kind];
  const className =
    'flex items-center gap-3 -mx-2 px-2 py-2 rounded hover:bg-surface-secondary transition-colors';

  const inner = (
    <>
      <div className="w-16 aspect-video rounded overflow-hidden bg-surface-tertiary shrink-0 flex items-center justify-center">
        {item.thumb ? (
          <img src={item.thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <Icon icon={meta.icon} className="text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.title}</p>
        <p className="text-xs text-muted">
          {meta.label}
          {item.date ? ` · ${formatDate(item.date)}` : ''}
        </p>
      </div>
    </>
  );

  return item.href ? (
    <a
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {inner}
    </a>
  ) : (
    <div className={className}>{inner}</div>
  );
};

const NextUp = ({
  teamSeasonId,
  owner
}: {
  teamSeasonId: number;
  owner: string;
}) => {
  const { t } = useTranslation();

  const params = { owner, team_season_id: String(teamSeasonId) };

  const { results: planResults } = useModelIndex('practice_plan', {
    filter: {
      team_season_id: teamSeasonId,
      plan_date: { gteq: todayIso() }
    },
    order: ['plan_date'],
    limit: 5,
    queryOptions: { enabled: !!teamSeasonId }
  });

  const { results: videoResults } = useModelIndex('video', {
    filter: {
      team_season_id: teamSeasonId,
      category: 'time_based',
      scheduled_at: { gteq_coalesce: todayIso() }
    },
    order: ['scheduled_at', '-created_at'],
    limit: 5,
    queryOptions: { enabled: !!teamSeasonId }
  });

  const plans = planResults ?? [];
  const videos = videoResults ?? [];

  const items: NextUpItem[] = [
    ...plans.map((p) => ({
      key: `plan-${p.id}`,
      kind: 'plan' as const,
      title: p.title as string,
      date: p.plan_date as string | null,
      href: p.pdf_attachment?.url as string | undefined,
      thumb: (
        p.pdf_attachment?.previews as { thumb?: { url?: string } } | undefined
      )?.thumb?.url
    })),
    ...videos.map((v) => ({
      key: `video-${v.id}`,
      kind: 'video' as const,
      title: v.title as string,
      date: v.scheduled_at as string | null,
      href: v.url as string | undefined,
      thumb: getYouTubeThumbnail(v.url) || undefined
    }))
  ]
    .sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999'))
    .slice(0, 6);

  return (
    <Card>
      <CardHeader title={t('frontend.messages.next_up')} />
      <Card.Content>
        {items.length > 0 ? (
          <ul>
            {items.map((item) => (
              <li key={item.key}>
                <NextUpRow item={item} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">
            {t('frontend.messages.nothing_scheduled_yet')}
          </p>
        )}
        <div className="flex gap-4 mt-3 text-sm">
          <Link
            to={'/$owner/active/$team_season_id/practice-plans' as never}
            params={params as never}
            className="text-accent hover:underline"
          >
            {t('frontend.messages.all_plans')}
          </Link>
          <Link
            to={'/$owner/active/$team_season_id/videos' as never}
            params={params as never}
            className="text-accent hover:underline"
          >
            {t('frontend.messages.all_prep')}
          </Link>
        </div>
      </Card.Content>
    </Card>
  );
};

/* ---------- schedule (only when a calendar is connected) ---------- */

type CalendarEvent = {
  uid: string;
  title: string;
  location: string | null;
  start_at: string;
};

const formatEventAt = (iso: string) =>
  new Date(iso).toLocaleString(getLocale().replaceAll('_', '-'), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

const ScheduleEvents = ({ teamSeasonId }: { teamSeasonId: number }) => {
  const { t } = useTranslation();

  const teamSeasonModel = useModel('team_season');

  const { data, isLoading } = useQuery({
    queryKey: ['calendarEvents', teamSeasonModel.path, teamSeasonId],
    queryFn: () =>
      networkApiCallOnlyData(
        `${teamSeasonModel.path}/${teamSeasonId}/calendar_events`
      ) as Promise<{ events: CalendarEvent[]; fetch_error?: string }>,
    enabled: !!teamSeasonId
  });

  if (isLoading) {
    return (
      <Typography.Paragraph color="muted">
        {t('frontend.messages.loading')}
      </Typography.Paragraph>
    );
  }

  // A feed that is briefly unreachable shouldn't put an error on the dashboard;
  // the calendar page is where a coach goes to find out why it's empty.
  const events = data?.events ?? [];
  if (events.length === 0) {
    return (
      <Typography.Paragraph color="muted">
        {t('frontend.messages.no_upcoming_events')}
      </Typography.Paragraph>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-separator">
      {events.slice(0, 5).map((event) => (
        <li
          key={`${event.uid}-${event.start_at}`}
          className="py-2 flex items-baseline justify-between gap-3"
        >
          <span className="text-sm truncate">{event.title}</span>
          <span className="text-sm text-muted whitespace-nowrap">
            {formatEventAt(event.start_at)}
          </span>
        </li>
      ))}
    </ul>
  );
};

// An ICS feed gives us a typed event list, so it wins here as it does on the
// portal; the embed is a calendar we can only hand to an iframe.
export const ScheduleCard = ({
  season,
  owner
}: {
  season: Pick<
    RhinoRead<'team_season'>,
    'id' | 'calendar_ics_url' | 'calendar_embed_url'
  >;
  owner: string;
}) => {
  const { t } = useTranslation();

  const icsUrl = season.calendar_ics_url as string | null;
  const embedUrl = season.calendar_embed_url as string | null;
  const params = { owner, team_season_id: String(season.id) };

  return (
    <Card>
      <CardHeader
        title={t('frontend.messages.schedule')}
        to="/$owner/active/$team_season_id/calendar"
        params={params}
      />
      <Card.Content>
        {icsUrl ? (
          <ScheduleEvents teamSeasonId={Number(season.id)} />
        ) : (
          <div className="rounded overflow-hidden border border-separator">
            <iframe
              src={embedUrl as string}
              title={t('frontend.messages.team_calendar_2')}
              className="w-full border-none"
              height={260}
              loading="lazy"
            />
          </div>
        )}
      </Card.Content>
    </Card>
  );
};

/* ---------- page ---------- */

const Dashboard = () => {
  const { t } = useTranslation();

  const state = useOverlayState();
  const organization = useBaseOwner();
  const baseOwnerId = useBaseOwnerId();
  const owner = String(baseOwnerId);
  const team = useCurrentTeam();
  const isAdmin = useHasRoleOf('admin');
  const extrasEnabled = useFeature('player_portal');
  const { resources: activeSeasons } = useActiveSeasons();

  const activeSeason = activeSeasons?.results?.[0];
  const teamSeasonId = Number(activeSeason?.id);
  const hasCalendar =
    extrasEnabled &&
    !!(activeSeason?.calendar_ics_url || activeSeason?.calendar_embed_url);

  if (!team) return null;

  return (
    <MaxWidth>
      <div className="flex justify-between items-start mb-8">
        <div>
          <Typography.Heading level={1}>
            {t('frontend.messages.current_season')}
          </Typography.Heading>
          <p className="text-muted">
            {organization?.name as string} &mdash; {team?.name}
            {activeSeason?.season?.name && (
              <>
                {' '}
                &middot;{' '}
                <span className="text-foreground/70">
                  {activeSeason.season.name}
                </span>
              </>
            )}
          </p>
        </div>
        {isAdmin && (
          <Button variant="primary" onPress={state.open}>
            {t('frontend.messages.add_season')}
          </Button>
        )}
      </div>

      {activeSeason ? (
        <>
          <div className="mb-8">
            <ActivityCard teamSeasonId={teamSeasonId} />
          </div>

          {extrasEnabled && (
            <div className="mb-8">
              <NextUp teamSeasonId={teamSeasonId} owner={owner} />
            </div>
          )}

          {hasCalendar && <ScheduleCard season={activeSeason} owner={owner} />}
        </>
      ) : (
        <Card className="p-6 text-center text-muted">
          {t('frontend.messages.no_active_seasons')}
        </Card>
      )}
      <SeasonSidebarForm isOpen={state.isOpen} onModalClose={state.close} />
    </MaxWidth>
  );
};

const DashboardPage = () => {
  const baseOwnerId = useBaseOwnerId();

  const { isSuccess, resources: teams } = useModelIndex('team', {
    filter: { organization_id: baseOwnerId },
    queryOptions: { enabled: !!baseOwnerId }
  });

  const isFullyLoaded = baseOwnerId && isSuccess;
  const hasTeam = teams?.total ?? 0 > 0;

  return (
    <BaseAuthedPage>
      {isFullyLoaded && <Dashboard />}
      <OtsSidebarForm
        isOpen={!!isFullyLoaded && !hasTeam}
        onModalClose={() => {}}
        isFirstTeam
      />
    </BaseAuthedPage>
  );
};

export default DashboardPage;
